import {getContent} from '@/services/content/content';
import {interpolate} from '@/services/localization/localizationService';
import type {ButtonModel, ExternalLinkExtendedModel, IconModel, MetricModel, MetricValueLabelModel} from '@/types/aem';

export interface BreakdownRow {
    label?: string;
    description?: string;
    icon?: IconModel;
    button?: ButtonModel;
    learnMoreExternalLink?: ExternalLinkExtendedModel;
    share: string;
}

interface LabelShare {
    valueLabel: MetricValueLabelModel;
    share: number;
}

const getMetric = (code: string) =>
    getContent().experienceScoreContent?.metrics?.find((metric) => metric.code === code);

/** A metric's explanation, or the whole score's one without a code. */
const getExplanation = (code?: string) =>
    code ? getMetric(code)?.explanation : getContent().experienceScoreContent?.explanation;

const hasExplanation = (code?: string) => Boolean(getExplanation(code));

const hasDetail = (code: string) => Boolean(getMetric(code)?.detail);

/** Breakdown metrics report a share per option: only options with an authored label count. */
const getLabelShares = (valueLabels: MetricValueLabelModel[] = [], breakdown?: object): LabelShare[] => {
    const shares = breakdown as Record<string, unknown> | undefined;

    return valueLabels.flatMap((valueLabel) => {
        const share = valueLabel.value ? shares?.[valueLabel.value] : undefined;

        return typeof share === 'number' ? [{valueLabel, share}] : [];
    });
};

/** Ties keep AEM order. */
const getDominantValueLabel = (valueLabels?: MetricValueLabelModel[], breakdown?: object) =>
    getLabelShares(valueLabels, breakdown).reduce<LabelShare | undefined>(
        (dominant, labelShare) => (!dominant || labelShare.share > dominant.share ? labelShare : dominant),
        undefined
    )?.valueLabel.label;

const getBreakdownRows = (metric?: MetricModel, breakdown?: object): BreakdownRow[] =>
    getLabelShares(metric?.valueLabels, breakdown).map(
        ({valueLabel: {label, description, icon, button, learnMoreExternalLink}, share}) => ({
            label,
            description,
            icon,
            button,
            learnMoreExternalLink,
            share: interpolate(metric?.explanation?.percentageFormat ?? '', Math.round(share)),
        })
    );

export default {
    getMetric,
    getExplanation,
    hasExplanation,
    hasDetail,
    getDominantValueLabel,
    getBreakdownRows,
};
